import {GenerateUUID} from "mobx-graphlink";
import {EngineConfig_MicroSnooze} from "../../../Server/DB/engineConfigs/@EngineConfig/@EC_MicroSnooze.js";
import {GetAudioFilesWithSubpath} from "../../../Store/main/general/CacheState.js";
import {PlaySound, PlaySound_StopAll, StopSound} from "../../../Utils/Bridge/Bridge_Native/MediaPlayer.js";
import {LiveSession, TriggerPackage} from "../../LiveSession.js";
import {AlarmsComp} from "./AlarmsComp.js";
import {EngineSessionComp} from "./EngineSessionComp.js";
import {BreathComp} from "./BreathComp.js";
import {GetPercentFromXToY, Timer} from "js-vextensions";
import {VisualizationComp} from "./VisualizationComp.js";
import {DreamTranscribeComp} from "./DreamTranscribeComp.js";
import {BackgroundComp} from "./BackgroundComp.js";
import {SoundPanningType} from "../../../Server/DB/@Shared/EffectPointer.js";

export class MicroSnoozeComp extends EngineSessionComp<EngineConfig_MicroSnooze> {
	constructor(session: LiveSession, config: EngineConfig_MicroSnooze) {
		super(session, config, s=>config.enabled, s=>s.IsLocal());
	}

	GetTriggerPackages() {
		return [
			new TriggerPackage("Alarms_MicroSnooze", this.c.microSnooze_triggerSet, this, {}, async triggerInfo=>{
				this.StartOrExtendSnooze(true);
			}),
		];
	}

	// players
	/*microSnooze_soundPlayer = new SoundPlayer();
	microSnooze_lightPlayer = new LightPlayer();*/

	// timers
	//microSnoozeEndTimer: Timer;

	OnStart() {
		//this.microSnoozeEndTimer = new Timer(this.c.maxTimeForProgress * 1000, ()=>this.EndMicroSnooze(), 1).SetContext(this.s.timerContext);
	}
	OnStop() {
		this.StopTiltSnooze(); // stop tilt-snooze, to ensure its audio is not left playing
		//this.EndSnoozeIfActive();
	}

	// level-1 (tilt-snooze, which activates the more general micro-snooze)
	// ==========

	lastTiltAngle = -1;
	tiltSnooze_startTime = -1; // -1 whenever tilt-snooze is not active (initially, and when phone not tilted, and when tilt-snooze has expired)
	tiltSnooze_playerId: string|n;
	OnTiltAngleReceived(tiltAngle_raw: number) {
		// convert raw 0-180 tilt-angle, to 0-90 tilt-angle, yielding 0 at both "flat on bed, screen up" [raw:0] and "flat on bed, screen down" [raw:180]
		const tiltAngle = Math.min(tiltAngle_raw, tiltAngle_raw.Distance(180));

		const prevIsTilted = this.c.tiltSnooze_enabled && this.lastTiltAngle >= this.c.tiltSnooze_minAngleFromFlat;
		const isTilted = this.c.tiltSnooze_enabled && tiltAngle >= this.c.tiltSnooze_minAngleFromFlat;
		if (isTilted && !prevIsTilted) {
			this.OnTilted();
		} else if (!isTilted && prevIsTilted) {
			this.OnUntilted()
		}

		// ongoing checks
		if (isTilted) {
			// if tilt-snooze is active (ie. not expired)
			if (this.tiltSnooze_startTime != -1) {
				// keep trying to do micro-snoozes (happens each tick, until tilt-snooze expires)
				this.StartOrExtendSnooze(false);

				// now check if the tilt-snooze should expire; if so, end the current tilt-snooze
				const tiltDuration = Date.now() - this.tiltSnooze_startTime;
				if (tiltDuration >= this.c.tiltSnooze_maxTiltDuration * 1000) {
					this.StopTiltSnooze();
				}
			}
		}

		this.lastTiltAngle = tiltAngle;
	}
	// handlers for when phone physically becomes tilted/untilted
	OnTilted() {
		// only start tilt-snooze if it's night (ie. if alarms are active)
		if (this.s.coreData.launchType == "night") {
			// If micro-snooze is blocked, also block start of higher-level tilt-snooze (since it's meaningless in that case, so its audio would just confuse).
			const microSnoozeBlocked = this.s.coreData.alarms_tillAlarmEnd_blockMicroSnooze;
			if (!microSnoozeBlocked) {
				this.StartTiltSnooze();
			}
		}
	}
	OnUntilted() {
		this.StopTiltSnooze();
	}
	// handlers for when a "tilt-snooze" starts/ends
	async StartTiltSnooze() {
		this.tiltSnooze_startTime = Date.now();
		const soundToPlay = GetAudioFilesWithSubpath(this.c.tiltSnooze_soundPath).Random();
		if (soundToPlay) {
			this.StopAndClearTiltSnoozePlayer();
			this.tiltSnooze_playerId = GenerateUUID();
			await PlaySound({entry: soundToPlay}, {
				volume: this.c.tiltSnooze_soundVolume,
				loop: true,
				playerId: this.tiltSnooze_playerId,
				// micro-snooze sound is often active while user is voice-logging; panning of that sound could be distracting, so disable it
				panningType: SoundPanningType.none,
			});
		}
	}
	StopTiltSnooze() {
		if (this.tiltSnooze_startTime == -1) return;
		this.tiltSnooze_startTime = -1;
		this.StopAndClearTiltSnoozePlayer();
	}
	StopAndClearTiltSnoozePlayer() {
		if (this.tiltSnooze_playerId) {
			StopSound(this.tiltSnooze_playerId);
			this.tiltSnooze_playerId = null;
		}
	}

	// level 2 (micro-snooze, which activates the more general alarms-comp "solve progress"
	// ==========

	/** Returns true if micro-snooze was successfully started. */
	StartOrExtendSnooze(updateBreathCompEffectsImmediately: boolean) {
		if (this.s.coreData.alarms_tillAlarmEnd_blockMicroSnooze) return false;

		// update snooze state/tracking-fields
		if (!this.snooze_active) {
			this.snooze_active = true;
			this.s.AsLocal!.AddEvent({type: "Snooze.SnoozeStart"});
			this.s.Comp(BackgroundComp).UpdateEffectsEnabled();
			this.s.Comp(VisualizationComp).UpdatePromptingEnabled();
			this.s.Comp(DreamTranscribeComp).UpdateTranscribeEnabled();
		}
		this.snooze_active_endTimer.Start(); // reset end-timer
		this.lastSnoozeExtensionTime = Math.max(this.lastSnoozeExtensionTime ?? 0, Date.now());

		// apply effects on alarms comp
		const alarmsComp = this.s.Comp(AlarmsComp);
		const priorPhase = alarmsComp.phase;
		const phaseAfterSolveFail_override = this.c.phaseAfterSolveFail_overrides?.[priorPhase] ?? null;
		alarmsComp.NotifySolveProgress(this.c.maxTimeForProgress, false, {phaseAfterSolveFail_override});

		// if we were in dreaming (ie. alarm-playing) phase before this micro-snooze, stop all instances of "play random sound from folder"
		// todo: replace this with a better/more-customizable system
		// note: commented out, as sometimes disrupted tilt-snooze sound; if causes new problem, find an actual/proper fix for that directly
		/*if (wasDreamingPhase) {
			PlaySound_StopAll();
		}*/

		const breathComp = this.s.Comp(BreathComp);
		if (updateBreathCompEffectsImmediately && breathComp?.active && breathComp.c.enabled) {
			breathComp.DoCalcAndUpdateEffects();
		}

		return true;
	}
	EndSnoozeIfActive() {
		if (!this.snooze_active) return;
		this.snooze_active = false;
		this.s.AsLocal!.AddEvent({type: "Snooze.SnoozeEnd"});
		this.s.Comp(BackgroundComp).UpdateEffectsEnabled();
		this.s.Comp(VisualizationComp).UpdatePromptingEnabled();
		this.s.Comp(DreamTranscribeComp).UpdateTranscribeEnabled();
	}

	snooze_active = false;
	snooze_active_endTimer = new Timer(this.c.maxTimeForProgress * 1000, ()=>{
		this.EndSnoozeIfActive();
	}, 1).SetContext(this.s.timerContext);
	lastSnoozeExtensionTime: number|n;
	GetTimeSinceSnoozeExtension() {
		if (this.lastSnoozeExtensionTime == null) return null;
		return Date.now() - this.lastSnoozeExtensionTime;
	}
	GetTimeTillSnoozeEnd() {
		if (this.lastSnoozeExtensionTime == null) return null;
		const snoozeDuration = this.c.maxTimeForProgress * 1000;
		return snoozeDuration - this.GetTimeSinceSnoozeExtension()!;
	}
	GetPercentThroughSnoozeEndTransition(transitionDurationMS: number) {
		if (this.lastSnoozeExtensionTime == null) return null;
		const timeTillSnoozeEnd = this.GetTimeTillSnoozeEnd()!;
		return GetPercentFromXToY(transitionDurationMS, 0, timeTillSnoozeEnd);
	}
}